import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Loader, Dimmer, Message, Segment } from 'semantic-ui-react';
import { DriverPerformanceHistoryDto } from '../../models/driver-performance-history';
import { usePerformanceHistoryService } from '../../services/backoffice/hooks/usePerformanceHistoryService';
import './DriverPerformanceChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DriverPerformanceChartProps {
  driverCpf: string;
  intervalStart: string;
  intervalEnd: string;
}

const DriverPerformanceChart: React.FC<DriverPerformanceChartProps> = ({ driverCpf, intervalStart, intervalEnd }) => {
  const [dailyData, setDailyData] = useState<DriverPerformanceHistoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { getDriverDailyPerformance } = usePerformanceHistoryService();

  useEffect(() => {
    if (!driverCpf || !intervalStart || !intervalEnd) {
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDriverDailyPerformance(
          driverCpf,
          intervalStart,
          intervalEnd
        );

        if (isMounted) {
          if (data && Array.isArray(data) && data.length > 0) {
            // Ordenar por data
            const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            setDailyData(sortedData);
          } else {
            setDailyData([]);
          }
        }
      } catch (err: any) {
        if (isMounted) {
          const errorMessage = err.response?.data?.message || err.message || 'Erro ao carregar dados de performance';
          setError(errorMessage);
          console.error('Erro ao buscar dados diários:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [driverCpf, intervalStart, intervalEnd]);

  if (loading) {
    return (
      <Segment className="chart-loading">
        <Dimmer active inverted>
          <Loader inverted>Carregando dados...</Loader>
        </Dimmer>
      </Segment>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Erro</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  if (!dailyData || dailyData.length === 0) {
    return (
      <Message info>
        <Message.Header>Sem dados</Message.Header>
        <p>Não há dados de performance para o período selecionado.</p>
      </Message>
    );
  }

  const labels = dailyData.map(d => {
    const date = new Date(d.date);
    return `${date.getUTCDate().toString().padStart(2, '0')}/${(date.getUTCMonth() + 1).toString().padStart(2, '0')}`;
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Inércia (%)',
        data: dailyData.map(d => d.inertiaPercentage),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: 'Carga Motor (%)',
        data: dailyData.map(d => d.motorLoadPercentage),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: 'rgb(239, 68, 68)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold' as const
        },
        bodyFont: {
          size: 13
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            label += context.parsed.y.toFixed(2) + '%';
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
          },
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        beginAtZero: true,
        max: 50,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 11,
            family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
          },
          callback: function(value: any) {
            return value + '%';
          }
        }
      }
    }
  };

  return (
    <div className="driver-performance-chart-container">
      <Line data={chartData} options={options} />
    </div>
  );
};

export default DriverPerformanceChart;

